import { ToolPinToggle } from '@/components/ToolPinToggle';
import { Pill } from '@/components/Pill';
import {
  CheckCircle2,
  Clock,
  ExternalLink,
  FolderOpen,
  Loader2,
  User,
} from 'lucide-react';

// Minimal OID -> display name map for project owners. Avoids surfacing raw
// Entra OIDs in the UI. Extend as more people own projects. Long-term this
// should be a denormalized owner_display_name column on projects.projects
// or a user lookup, but for the current 3-project state a hand-maintained
// map is the simplest correct thing.
const OWNER_OID_TO_NAME: Record<string, string> = {
  '19e9b23b-d8fa-4801-bffb-cf36db25b87f': 'Steven Pinto',
};

import { useSession } from '@/lib/useSession';
import { formatDate } from '@/lib/dateUtils';
import {
  hasProjectsRead,
  projectProgressPercent,
  useProjects,
  type ProjectListItem,
  type ProjectStatus,
} from '@/lib/useProjects';

const STATUS_LABEL: Record<ProjectStatus, string> = {
  draft: 'Draft',
  in_review: 'In review',
  in_progress: 'In progress',
  blocked: 'Blocked',
  done: 'Done',
  archived: 'Archived',
};

const STATUS_TONE: Record<ProjectStatus, string> = {
  draft:
    'bg-amp-cream-warm text-amp-ink-muted dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink-muted',
  in_review:
    'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  in_progress:
    'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  blocked: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  done: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  archived:
    'bg-amp-cream text-amp-ink-muted dark:bg-amp-dark-surface dark:text-amp-dark-ink-muted',
};

function StatusBadge({ status }: { status: ProjectStatus }) {
  return <Pill tone={STATUS_TONE[status]}>{STATUS_LABEL[status]}</Pill>;
}

function ProgressBar({ pct, done, total }: { pct: number; done: number; total: number }) {
  return (
    <div className="mb-3">
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-amp-ink-muted dark:text-amp-dark-ink-muted">
          {done}/{total} tasks
        </span>
        <span className="font-medium text-amp-ink dark:text-amp-dark-ink">{pct}%</span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-amp-cream dark:bg-amp-dark-surface-warm">
        <div
          className="h-1.5 rounded-full bg-amp-olive dark:bg-amp-olive-dark transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function StakeholderList({ p }: { p: ProjectListItem }) {
  const stakeholders = p.stakeholders ?? [];
  if (stakeholders.length === 0) return null;
  return (
    <ul className="mb-3 space-y-0.5">
      {stakeholders.map((s) => (
        <li
          key={s.id}
          className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted"
        >
          <span className="text-amp-ink dark:text-amp-dark-ink">{s.display_name}</span>
          {' — '}
          {s.role}
        </li>
      ))}
    </ul>
  );
}

function DocLinks({ p }: { p: ProjectListItem }) {
  const userDoc = p.confluence_user_doc_url;
  const troubleshoot = p.confluence_troubleshoot_url;
  if (!userDoc && !troubleshoot) return null;
  return (
    <div className="flex flex-wrap gap-2 mb-3">
      {userDoc ? (
        <a
          href={userDoc}
          target="_blank"
          rel="noreferrer noopener"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1 text-xs text-amp-olive dark:text-amp-olive-dark hover:underline"
        >
          End-user docs <ExternalLink className="h-3 w-3" />
        </a>
      ) : null}
      {troubleshoot ? (
        <a
          href={troubleshoot}
          target="_blank"
          rel="noreferrer noopener"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1 text-xs text-amp-olive dark:text-amp-olive-dark hover:underline"
        >
          Troubleshooting <ExternalLink className="h-3 w-3" />
        </a>
      ) : null}
    </div>
  );
}

function ProjectCard({ p }: { p: ProjectListItem }) {
  // The card body click-through goes straight to the SharePoint folder if one
  // is set on the project. Detail-page route was removed (2026-05-23 refactor):
  // the card view is the only project surface.
  const href = p.sharepoint_folder_url ?? undefined;
  const baseClasses =
    'block border border-amp-line rounded-lg bg-amp-white p-4 transition-colors dark:border-amp-dark-line dark:bg-amp-dark-surface';
  const interactive = href
    ? 'hover:border-amp-olive dark:hover:border-amp-olive-dark cursor-pointer'
    : 'cursor-default';

  const inner = (
    <>
      <div className="flex items-start justify-between gap-2 mb-2">
        <h3 className="text-[16px] font-semibold text-amp-ink dark:text-amp-dark-ink line-clamp-1">
          {p.name}
        </h3>
        <StatusBadge status={p.status} />
      </div>
      <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted line-clamp-2 mb-3 min-h-[2.5rem]">
        {p.summary}
      </p>
      <ProgressBar pct={projectProgressPercent(p)} done={p.task_done} total={p.task_count} />
      <StakeholderList p={p} />
      <DocLinks p={p} />
      <div className="mt-1 flex flex-wrap items-center justify-between gap-2 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
        <span className="inline-flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {p.ended_at ? `Ended ${formatDate(p.ended_at)}` : `Started ${formatDate(p.started_at)}`}
        </span>
        <span className="inline-flex items-center gap-2">
          {p.owner_oid && OWNER_OID_TO_NAME[p.owner_oid] ? (
            <span className="inline-flex items-center gap-1">
              <User className="h-3 w-3" />
              {OWNER_OID_TO_NAME[p.owner_oid]}
            </span>
          ) : null}
          {p.approved ? (
            <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-300">
              <CheckCircle2 className="h-3 w-3" />
              Approved
            </span>
          ) : null}
        </span>
      </div>
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer noopener"
        className={`${baseClasses} ${interactive}`}
      >
        {inner}
      </a>
    );
  }
  return <div className={`${baseClasses} ${interactive}`}>{inner}</div>;
}

function AccessDeniedCard() {
  return (
    <div className="border border-amp-line rounded-lg bg-amp-white p-6 dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <h2 className="text-[16px] font-semibold text-amp-ink dark:text-amp-dark-ink mb-2">
        Sign in to view projects.
      </h2>
      <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
        Cockpit Projects is available to any signed-in employee.
      </p>
    </div>
  );
}

export function Projects() {
  const { user } = useSession();
  const groups = user?.groups ?? [];
  const isViewer = hasProjectsRead(groups);
  const projects = useProjects();

  if (!user) {
    return (
      <div>
        <Header />
        <AccessDeniedCard />
      </div>
    );
  }
  if (!isViewer) {
    return (
      <div>
        <Header />
        <AccessDeniedCard />
      </div>
    );
  }

  return (
    <div>
      <Header />
      {projects.isLoading ? (
        <div className="border border-amp-line rounded-lg bg-amp-white p-6 text-sm text-amp-ink-muted dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink-muted inline-flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading projects...
        </div>
      ) : projects.error ? (
        <div className="border border-red-300 rounded-lg bg-red-50 p-6 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/40 dark:text-red-200">
          Could not load projects: {projects.error.message}
        </div>
      ) : projects.projects.length === 0 ? (
        <div className="border border-amp-line rounded-lg bg-amp-white p-6 dark:border-amp-dark-line dark:bg-amp-dark-surface">
          <div className="flex items-center gap-3 mb-2">
            <FolderOpen className="h-5 w-5 text-amp-olive dark:text-amp-olive-dark" />
            <h2 className="text-[16px] font-semibold text-amp-ink dark:text-amp-dark-ink">
              No projects yet
            </h2>
          </div>
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
            Once a project is created via the API or Claude pulls a spec from
            SharePoint, it will show up here.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.projects.map((p) => (
            <ProjectCard key={p.id} p={p} />
          ))}
        </div>
      )}
    </div>
  );
}

function Header() {
  return (
    <>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">
            Cockpit
          </p>
          <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">
            Projects
          </h1>
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
            Active project specs. Card click-through opens the SharePoint folder. Auto-refresh every 30 seconds.
          </p>
        </div>
        <ToolPinToggle toolSlug="projects" toolName="Projects" />
      </div>
    </>
  );
}
